import { useState, useEffect, useCallback, useMemo } from "react";
import {
  saveProjects,
  loadProjects,
  saveTags,
  loadTags,
} from "../utils/organizationStorage";
import {
  getProjects as fetchProjects,
  createProject as createProjectAPI,
  updateProject as updateProjectAPI,
  deleteProject as deleteProjectAPI,
  getTags as fetchTags,
  createTag as createTagAPI,
  updateTag as updateTagAPI,
  deleteTag as deleteTagAPI,
  updateSongOrganization as updateSongOrgAPI,
} from "../services/organizationService";
import { saveUserSongs } from "../utils/songStorage";
import { httpPost } from "../utils/http";
import { API_BASE_URL } from "../config/api";

const FREE_PROJECT_LIMIT = 3;
const FREE_TAG_LIMIT = 5;

const useOrganization = ({
  isAuthenticated,
  user,
  storageType,
  songs,
  setSongs,
}) => {
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeProjectFilter, setActiveProjectFilter] = useState(null);
  const [activeTagFilters, setActiveTagFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectLimit, setProjectLimit] = useState(FREE_PROJECT_LIMIT);
  const [tagLimit, setTagLimit] = useState(FREE_TAG_LIMIT);
  const [recordingDurationSec, setRecordingDurationSec] = useState(120);

  // Compute song counts from the songs array
  const songCounts = useMemo(() => {
    const counts = { unorganized: 0 };
    if (!songs) return counts;

    for (const song of songs) {
      if (song.isExample) continue;

      // Project counts
      if (song.projectId) {
        counts[song.projectId] = (counts[song.projectId] || 0) + 1;
      } else {
        counts.unorganized++;
      }

      // Tag counts
      const songTagIds =
        song.tagIds || (song.tags ? song.tags.map((t) => t.id) : []);
      for (const tagId of songTagIds) {
        counts[tagId] = (counts[tagId] || 0) + 1;
      }
    }
    return counts;
  }, [songs]);

  const canCreateMoreProjects = projects.length < projectLimit;
  const canCreateMoreTags = tags.length < tagLimit;

  // Load organization data
  const loadOrganization = useCallback(async () => {
    setIsLoading(true);
    try {
      if (storageType === "database" && isAuthenticated) {
        const [projectsRes, tagsRes] = await Promise.all([
          fetchProjects(),
          fetchTags(),
        ]);
        setProjects(projectsRes.projects || []);
        setTags(tagsRes.tags || []);

        // Get limits from subscription check
        if (projectsRes.limit) setProjectLimit(projectsRes.limit);
        if (tagsRes.limit) setTagLimit(tagsRes.limit);
      } else {
        setProjects(loadProjects());
        setTags(loadTags());
        setProjectLimit(FREE_PROJECT_LIMIT);
        setTagLimit(FREE_TAG_LIMIT);
      }
    } catch (error) {
      console.error("Failed to load organization:", error);
    } finally {
      setIsLoading(false);
    }
  }, [storageType, isAuthenticated]);

  // Load on mount and when storage/auth changes
  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  // Also fetch limits from check-subscription when authenticated
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const fetchLimits = async () => {
      try {
        const AUTH_API = API_BASE_URL.replace("/.netlify/functions", "");
        const response = await httpPost(
          `${AUTH_API}/.netlify/functions/check-subscription`,
          { userId: user.id },
        );
        if (response.ok) {
          const data = await response.json();
          if (data.projectLimit) setProjectLimit(data.projectLimit);
          if (data.tagLimit) setTagLimit(data.tagLimit);
          if (data.recordingDurationSec)
            setRecordingDurationSec(data.recordingDurationSec);
        }
      } catch (error) {
        console.warn("Failed to fetch subscription limits:", error);
      }
    };

    fetchLimits();
  }, [isAuthenticated, user]);

  // --- Project CRUD ---

  const createProject = useCallback(
    async (name, color) => {
      if (storageType === "database" && isAuthenticated) {
        const result = await createProjectAPI(name, color);
        setProjects((prev) => [...prev, result.project]);
        return result.project;
      } else {
        const newProject = {
          id: `local-${Date.now()}`,
          name: name.trim(),
          color: color || null,
          sortOrder: projects.length,
          songCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const updated = [...projects, newProject];
        setProjects(updated);
        saveProjects(updated);
        return newProject;
      }
    },
    [storageType, isAuthenticated, projects],
  );

  const updateProject = useCallback(
    async (id, data) => {
      if (storageType === "database" && isAuthenticated) {
        const result = await updateProjectAPI(id, data);
        setProjects((prev) =>
          prev.map((p) => (p.id === id ? { ...p, ...result.project } : p)),
        );
      } else {
        const updated = projects.map((p) =>
          p.id === id
            ? { ...p, ...data, updatedAt: new Date().toISOString() }
            : p,
        );
        setProjects(updated);
        saveProjects(updated);
      }
    },
    [storageType, isAuthenticated, projects],
  );

  const deleteProject = useCallback(
    async (id) => {
      const originalProjects = [...projects];

      // Optimistic removal
      setProjects((prev) => prev.filter((p) => p.id !== id));

      try {
        if (storageType === "database" && isAuthenticated) {
          await deleteProjectAPI(id);
          // ON DELETE SET NULL handled by DB
        } else {
          const updated = projects.filter((p) => p.id !== id);
          saveProjects(updated);

          // Unassign songs from deleted project locally
          if (setSongs) {
            setSongs((prev) => {
              const updatedSongs = prev.map((s) =>
                s.projectId === id ? { ...s, projectId: null } : s,
              );
              saveUserSongs(updatedSongs);
              return updatedSongs;
            });
          }
        }
      } catch (error) {
        console.error("Failed to delete project:", error);
        setProjects(originalProjects);
        throw error;
      }

      // Clear filter if we just deleted the filtered project
      if (activeProjectFilter === id) {
        setActiveProjectFilter(null);
      }
    },
    [storageType, isAuthenticated, projects, activeProjectFilter, setSongs],
  );

  // --- Tag CRUD ---

  const createTag = useCallback(
    async (name, color) => {
      if (storageType === "database" && isAuthenticated) {
        const result = await createTagAPI(name, color);
        setTags((prev) => [...prev, result.tag]);
        return result.tag;
      } else {
        const newTag = {
          id: `local-${Date.now()}`,
          name: name.trim(),
          color: color || null,
          songCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        const updated = [...tags, newTag];
        setTags(updated);
        saveTags(updated);
        return newTag;
      }
    },
    [storageType, isAuthenticated, tags],
  );

  const updateTag = useCallback(
    async (id, data) => {
      if (storageType === "database" && isAuthenticated) {
        const result = await updateTagAPI(id, data);
        setTags((prev) =>
          prev.map((t) => (t.id === id ? { ...t, ...result.tag } : t)),
        );
      } else {
        const updated = tags.map((t) =>
          t.id === id
            ? { ...t, ...data, updatedAt: new Date().toISOString() }
            : t,
        );
        setTags(updated);
        saveTags(updated);
      }
    },
    [storageType, isAuthenticated, tags],
  );

  const deleteTag = useCallback(
    async (id) => {
      const originalTags = [...tags];

      // Optimistic removal
      setTags((prev) => prev.filter((t) => t.id !== id));

      try {
        if (storageType === "database" && isAuthenticated) {
          await deleteTagAPI(id);
          // CASCADE removes song_tags rows
        } else {
          const updated = tags.filter((t) => t.id !== id);
          saveTags(updated);

          // Remove tag from songs locally
          if (setSongs) {
            setSongs((prev) => {
              const updatedSongs = prev.map((s) => {
                if (s.tagIds && s.tagIds.includes(id)) {
                  return { ...s, tagIds: s.tagIds.filter((tid) => tid !== id) };
                }
                return s;
              });
              saveUserSongs(updatedSongs);
              return updatedSongs;
            });
          }
        }
      } catch (error) {
        console.error("Failed to delete tag:", error);
        setTags(originalTags);
        throw error;
      }

      // Clear filter if we just deleted a filtered tag
      if (activeTagFilters.includes(id)) {
        setActiveTagFilters((prev) => prev.filter((tid) => tid !== id));
      }
    },
    [storageType, isAuthenticated, tags, activeTagFilters, setSongs],
  );

  // --- Song Organization ---

  const updateSongOrganization = useCallback(
    async (songId, { projectId, tagIds }) => {
      try {
        if (storageType === "database" && isAuthenticated) {
          await updateSongOrgAPI(songId, { projectId, tagIds });
        }
      } catch (error) {
        console.error("Failed to update song organization:", error);
        throw error;
      }

      // Update local song state (only reached if API succeeded or local mode)
      if (setSongs) {
        setSongs((prev) => {
          const updatedSongs = prev.map((s) => {
            if (s.id === songId) {
              // Resolve project name/color from projects array
              const project = projects.find((p) => p.id === projectId);
              // Resolve tags from tags array
              const resolvedTags = (tagIds || [])
                .map((tid) => tags.find((t) => t.id === tid))
                .filter(Boolean);

              return {
                ...s,
                projectId: projectId || null,
                projectName: project ? project.name : null,
                projectColor: project ? project.color : null,
                tags: resolvedTags,
                tagIds: tagIds || [],
              };
            }
            return s;
          });

          // If local mode, persist to storage
          if (storageType === "local" || !isAuthenticated) {
            saveUserSongs(updatedSongs);
          }

          return updatedSongs;
        });
      }
    },
    [storageType, isAuthenticated, projects, tags, setSongs],
  );

  // --- Filtering ---

  const setProjectFilter = useCallback((id) => {
    setActiveProjectFilter((prev) => (prev === id ? null : id));
  }, []);

  const toggleTagFilter = useCallback((id) => {
    setActiveTagFilters((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id],
    );
  }, []);

  const clearFilters = useCallback(() => {
    setActiveProjectFilter(null);
    setActiveTagFilters([]);
  }, []);

  const getFilteredSongs = useCallback(
    (songsToFilter) => {
      if (!songsToFilter) return [];

      let filtered = songsToFilter;

      // Project filter
      if (activeProjectFilter === "unorganized") {
        filtered = filtered.filter((s) => !s.projectId);
      } else if (activeProjectFilter) {
        filtered = filtered.filter((s) => s.projectId === activeProjectFilter);
      }

      // Tag filter (intersection: songs must have ALL active tags)
      if (activeTagFilters.length > 0) {
        filtered = filtered.filter((s) => {
          const songTagIds =
            s.tagIds || (s.tags ? s.tags.map((t) => t.id) : []);
          return activeTagFilters.every((tid) => songTagIds.includes(tid));
        });
      }

      return filtered;
    },
    [activeProjectFilter, activeTagFilters],
  );

  return {
    // State
    projects,
    tags,
    activeProjectFilter,
    activeTagFilters,
    songCounts,
    isLoading,
    projectLimit,
    tagLimit,
    recordingDurationSec,
    canCreateMoreProjects,
    canCreateMoreTags,

    // Project CRUD
    createProject,
    updateProject,
    deleteProject,

    // Tag CRUD
    createTag,
    updateTag,
    deleteTag,

    // Song organization
    updateSongOrganization,

    // Filtering
    setProjectFilter,
    toggleTagFilter,
    clearFilters,
    getFilteredSongs,

    // Reload
    loadOrganization,
  };
};

export default useOrganization;
